<template>
  <v-col class="pa-0 widget-layout" data-testid="widget-layout">
    <v-app-bar
      v-show="showHeader"
      id="step-header"
      color="transparent"
      elevation="0"
      class="unset-height-app-bar"
    >
      <v-col class="pa-0">
        <slot name="alert" />
        <slot name="header" />
      </v-col>
    </v-app-bar>
    <v-row
      id="step-container"
      class="ma-0 flex h-100"
      align="center"
      justify="center"
      :class="{
        'no-line-height': stepIsFullScreen,
      }"
      :style="{
        minHeight: zohoChatIsEnabled || stepIsFullScreen ? '530px' : '400px',
      }"
      :data-testid="`step-${activeRouteName}`"
    >
      <v-col
        align="center"
        justify="center"
        cols="12"
        class="w-100"
        :class="innerClasses"
      >
        <slot name="body" />
      </v-col>
    </v-row>

    <v-footer
      v-show="showFooter"
      id="step-footer"
      ref="footer"
      :elevation="0"
      color="transparent"
      align-items="center"
    >
      <slot name="footer" />
    </v-footer>

    <div v-if="$slots.chat" id="widget-chat-parent">
      <div id="widget-chat" :style="chatStyle">
        <div id="chat">
          <slot name="chat" />
        </div>
      </div>
    </div>
  </v-col>
</template>

<script>
import { mapState } from "pinia";
import { useFlowStore } from "#src/stores/flow";
import { WidgetCommunicatorService } from "#src/services/widget-communicator.service";
import { useDisplay, useTheme } from "vuetify";
import { APPLY_ROUTE_META_NAMES } from "#src/enumerations/step-enumeration";
import { useInstanceSettingsStore } from "#src/stores/instance-settings";

export default {
  props: {
    showFooter: Boolean,
    showHeader: Boolean,
  },
  setup() {
    const { lgAndUp, smAndDown, mdAndDown } = useDisplay();
    const theme = useTheme();
    const widgetCommunicatorService = new WidgetCommunicatorService();
    return { lgAndUp, smAndDown, mdAndDown, theme, widgetCommunicatorService };
  },
  data: () => ({
    footerHeight: 64,
    headerHeight: 64,
    contentHeight: 492,
    resizeTimer: null,
    resizerRef: null,
    hasLoadedZoho: false,
    waitingForZoho: false,
  }),
  computed: {
    ...mapState(useInstanceSettingsStore, ["zohoChatIsEnabled"]),
    ...mapState(useFlowStore, ["activeStep"]),
    stepIsFullScreen() {
      return this.activeStep?.isFullscreen;
    },
    activeRouteName() {
      if (!this.activeStep?.step) return "";
      return APPLY_ROUTE_META_NAMES[this.activeStep.step];
    },
    innerClasses() {
      if (this.stepIsFullScreen) return "pa-0 align-self-stretch flex";
      else if (this.smAndDown) return "pa-1";
      return "";
    },
    chatStyle() {
      return {
        transform: `translate(-20px, calc(-100% - ${this.footerHeight}px))`,
      };
    },
  },
  mounted() {
    this.initializeResizeObservers();
  },
  unmounted() {
    if (this.resizerRef?.disconnect) this.resizerRef.disconnect();
  },
  methods: {
    waitForElement(selector) {
      return new Promise(resolve => {
        if (document.querySelector(selector)) {
          return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(() => {
          if (document.querySelector(selector)) {
            resolve(document.querySelector(selector));
            observer.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    },
    setZohoPosition() {
      const el = document.querySelector('[data-id="zsalesiq"]');
      if (el) {
        el.style.bottom = `${this.footerHeight + 20}px`;
        return;
      }

      if (this.waitingForZoho) return;
      this.waitingForZoho = true;
      this.waitForElement('[data-id="zsalesiq"]').then(() => {
        this.hasLoadedZoho = true;
        this.waitingForZoho = false;
        this.setZohoPosition();
      });
    },
    async initializeResizeObservers() {
      const headerEl = await this.waitForElement("#step-header");
      const footerEl = await this.waitForElement("#step-footer");
      const bodyEl = await this.waitForElement("#step-container");

      // ResizeObserver
      // Wrap with a setTimeout of 0 to occur after the loop
      // https://github.com/juggle/resize-observer/issues/103#issuecomment-1711148285
      const resizeHandler = () => {
        setTimeout(() => {
          if ([headerEl, footerEl, bodyEl].every(v => v.clientHeight === 0)) {
            this.resizerRef.disconnect();
            this.initializeResizeObservers();
            return;
          }
          this.headerHeight = headerEl.clientHeight;
          this.footerHeight = footerEl.clientHeight;
          this.contentHeight = bodyEl.clientHeight;

          if (this.zohoChatIsEnabled) this.setZohoPosition();

          let bodyHeight = this.contentHeight;
          if (this.showFooter) bodyHeight += this.footerHeight;
          if (this.showHeader) bodyHeight += this.headerHeight;
          this.widgetCommunicatorService.resizeIframe(Math.ceil(bodyHeight));
        }, 0);
      };
      this.resizerRef = new ResizeObserver(resizeHandler);
      this.resizerRef.observe(bodyEl);
      this.resizerRef.observe(footerEl);
      this.resizerRef.observe(headerEl);
    },
  },
};
</script>

<style lang="scss">
.widget-layout {
  #step-header {
    z-index: 10 !important;
    position: relative !important;
  }

  #step-footer {
    width: 100% !important;
    position: relative !important;
  }

  #step-container {
    scroll-behavior: smooth;
  }

  #widget-chat-parent {
    position: relative;

    #widget-chat {
      transform-origin: bottom right;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 21;
    }
  }
}
</style>
